import React, { useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { TIDS } from "@/constants/testIds";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createCustomQuote } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Send, Loader2 } from "lucide-react";

const Configurator = () => {
  const { t } = useI18n();
  const [hours, setHours] = useState([5]);
  const [complexity, setComplexity] = useState("intermedio");
  const [urgency, setUrgency] = useState("standard");
  const [description, setDescription] = useState("");
  const [budget, setBudget] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !description) {
      toast.error(t.lang === "en" ? "Please fill the required fields." : "Compila i campi obbligatori.");
      return;
    }
    setSubmitting(true);
    try {
      await createCustomQuote({
        name, email, company: company || null, hours: hours[0], complexity, urgency, budget: budget || null, description,
      });
      toast.success(t.configurator.success);
      setDescription(""); setBudget(""); setName(""); setEmail(""); setCompany("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante l'invio. Riprova.");
    } finally { setSubmitting(false); }
  };

  return (
    <section id="custom" className="py-20 md:py-32 bg-[#F5F7FA]" data-testid={TIDS.configuratorSection}>
      <div className="zdc-container">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#E5E7EB] mb-4">
              <Sparkles className="w-3.5 h-3.5 text-[#2563EB]" />
              <span className="text-xs font-medium text-[#111111]">{t.configurator.eyebrow}</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#111111]">
              {t.configurator.title}
            </h2>
            <p className="mt-4 text-base md:text-lg text-[#6B7280]">{t.configurator.subtitle}</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-[#E5E7EB] p-6 md:p-10 shadow-[0_10px_40px_rgba(17,17,17,0.04)] space-y-7">
            <div>
              <div className="flex items-baseline justify-between mb-3">
                <Label className="text-sm font-medium text-[#111111]">{t.configurator.hours}</Label>
                <span className="font-display text-2xl font-semibold text-[#2563EB]">{hours[0]}h</span>
              </div>
              <Slider
                data-testid={TIDS.configHours}
                value={hours}
                onValueChange={setHours}
                min={1}
                max={80}
                step={1}
                className="w-full"
              />
              <div className="mt-2 flex justify-between text-[10px] text-[#6B7280]">
                <span>1h</span><span>80h</span>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#111111]">{t.configurator.complexity}</Label>
                <Select value={complexity} onValueChange={setComplexity}>
                  <SelectTrigger data-testid={TIDS.configComplexity} className="rounded-xl h-11 border-[#E5E7EB]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(t.configurator.complexityOptions).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#111111]">{t.configurator.urgency}</Label>
                <Select value={urgency} onValueChange={setUrgency}>
                  <SelectTrigger data-testid={TIDS.configUrgency} className="rounded-xl h-11 border-[#E5E7EB]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(t.configurator.urgencyOptions).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#111111]">{t.configurator.description} *</Label>
              <Textarea
                data-testid={TIDS.configDescription}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t.configurator.descriptionPlaceholder}
                className="rounded-xl border-[#E5E7EB] min-h-[110px] resize-none"
                required
              />
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#111111]">{t.configurator.budget}</Label>
                <Input
                  data-testid={TIDS.configBudget}
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  placeholder={t.configurator.budgetPlaceholder}
                  className="rounded-xl h-11 border-[#E5E7EB]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#111111]">{t.configurator.company}</Label>
                <Input
                  data-testid={TIDS.configCompany}
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="rounded-xl h-11 border-[#E5E7EB]"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#111111]">{t.configurator.name} *</Label>
                <Input
                  data-testid={TIDS.configName}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="rounded-xl h-11 border-[#E5E7EB]"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#111111]">{t.configurator.email} *</Label>
                <Input
                  data-testid={TIDS.configEmail}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-xl h-11 border-[#E5E7EB]"
                  required
                />
              </div>
            </div>

            <div className="pt-2 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 p-5 rounded-xl bg-[#F5F7FA] border border-[#E5E7EB]">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-0.5">Total</div>
                <div className="font-display text-xl font-semibold text-[#111111]">
                  {t.configurator.priceLabel}
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid={TIDS.configSubmit}
                className="btn-blue justify-center disabled:opacity-60"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {t.configurator.submit}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Configurator;
